/**
 * Accept Invitation Page (Story 1.13)
 *
 * Public route that handles team invitation acceptance.
 * URL: /accept-invitation?token=xxx
 *
 * Scenarios:
 * - Authenticated user with matching email → auto-accepts, shows success
 * - Authenticated user with wrong email → shows email mismatch message
 * - Not authenticated, email has account → prompts login
 * - Not authenticated, email has no account → prompts registration
 * - Invalid/expired/revoked token → shows error
 */

import { useState, useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, CardContent, Button } from "@provenleap/ui";
import { apiPost } from "../lib/apiClient";
import { useAuthStore } from "../stores/authStore";

export interface AcceptSearchParams {
  token?: string;
}

export const Route = createFileRoute("/accept-invitation")({
  validateSearch: (search: Record<string, unknown>): AcceptSearchParams => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: AcceptInvitationPage,
});

type PageState =
  | { type: "loading" }
  | { type: "no-token" }
  | { type: "accepted"; tenantName: string; role: string }
  | { type: "already_member"; tenantName: string; role: string }
  | { type: "login"; email: string; tenantName: string; token: string }
  | { type: "register"; email: string; tenantName: string; token: string }
  | { type: "error"; message: string };

function AcceptInvitationPage() {
  const { token } = Route.useSearch();
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  const [state, setState] = useState<PageState>({ type: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ type: "no-token" });
      return;
    }

    let cancelled = false;

    async function acceptInvitation() {
      try {
        const res = await apiPost("/api/team/invitations/accept", { token });
        if (cancelled) return;

        if (!res.ok) {
          const err = await res.json();
          const message =
            (err as { errors?: Array<{ message: string }> }).errors?.[0]
              ?.message ??
            (err as { detail?: string }).detail ??
            "This invitation is no longer valid.";
          setState({ type: "error", message });
          return;
        }

        const { data } = (await res.json()) as {
          data: {
            action: string;
            email?: string;
            tenantName?: string;
            tenantId?: string;
            role?: string;
          };
        };

        switch (data.action) {
          case "accepted":
            setState({
              type: "accepted",
              tenantName: data.tenantName ?? "the team",
              role: data.role ?? "member",
            });
            break;
          case "already_member":
            setState({
              type: "already_member",
              tenantName: data.tenantName ?? "the team",
              role: data.role ?? "member",
            });
            break;
          case "login":
            setState({
              type: "login",
              email: data.email ?? "",
              tenantName: data.tenantName ?? "the team",
              token: token!,
            });
            break;
          case "register":
            setState({
              type: "register",
              email: data.email ?? "",
              tenantName: data.tenantName ?? "the team",
              token: token!,
            });
            break;
          default:
            setState({ type: "error", message: "Unexpected response." });
        }
      } catch {
        if (!cancelled) {
          setState({
            type: "error",
            message: "Something went wrong. Please try again.",
          });
        }
      }
    }

    acceptInvitation();
    return () => {
      cancelled = true;
    };
  }, [token, isAuthenticated]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {state.type === "loading" && (
          <Card>
            <CardContent className="p-6 text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Processing invitation...</p>
            </CardContent>
          </Card>
        )}

        {state.type === "no-token" && (
          <Card>
            <CardContent className="p-6 text-center space-y-4">
              <ErrorIcon />
              <h1 className="text-xl font-bold">Invalid Invitation Link</h1>
              <p className="text-muted-foreground">
                This invitation link is missing the required token. Please check
                your email for the correct link.
              </p>
              <Link to="/">
                <Button variant="outline">Go Home</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {state.type === "accepted" && (
          <Card>
            <CardContent className="p-6 text-center space-y-4">
              <SuccessIcon />
              <h1 className="text-xl font-bold text-green-700 dark:text-green-400">
                Invitation Accepted
              </h1>
              <p className="text-muted-foreground">
                You've joined <strong>{state.tenantName}</strong> as{" "}
                <strong>{state.role}</strong>.
              </p>
              <Link to="/dashboard">
                <Button>Go to Dashboard</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {state.type === "already_member" && (
          <Card>
            <CardContent className="p-6 text-center space-y-4">
              <SuccessIcon />
              <h1 className="text-xl font-bold">Already a Member</h1>
              <p className="text-muted-foreground">
                You're already a member of <strong>{state.tenantName}</strong>{" "}
                with the <strong>{state.role}</strong> role.
              </p>
              <Link to="/dashboard">
                <Button>Go to Dashboard</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {state.type === "login" && (
          <Card>
            <CardContent className="p-6 text-center space-y-4">
              <MailIcon />
              <h1 className="text-xl font-bold">Sign In to Accept</h1>
              <p className="text-muted-foreground">
                You've been invited to join{" "}
                <strong>{state.tenantName}</strong>. Sign in with{" "}
                <strong>{state.email}</strong> to accept this invitation.
              </p>
              <Link
                to="/login"
                search={{ redirect: `/accept-invitation?token=${state.token}` }}
              >
                <Button>Sign In</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {state.type === "register" && (
          <Card>
            <CardContent className="p-6 text-center space-y-4">
              <MailIcon />
              <h1 className="text-xl font-bold">Create Account to Join</h1>
              <p className="text-muted-foreground">
                You've been invited to join{" "}
                <strong>{state.tenantName}</strong>. Create an account with{" "}
                <strong>{state.email}</strong> to accept this invitation.
              </p>
              <Link
                to="/register"
                search={{ redirect: `/accept-invitation?token=${state.token}` }}
              >
                <Button>Create Account</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {state.type === "error" && (
          <Card>
            <CardContent className="p-6 text-center space-y-4">
              <ErrorIcon />
              <h1 className="text-xl font-bold text-destructive">
                Invitation Error
              </h1>
              <p className="text-muted-foreground">{state.message}</p>
              <Link to="/">
                <Button variant="outline">Go Home</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function SuccessIcon() {
  return (
    <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-8 w-8 text-green-600 dark:text-green-400"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    </div>
  );
}

function ErrorIcon() {
  return (
    <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-8 w-8 text-red-600 dark:text-red-400"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    </div>
  );
}

function MailIcon() {
  return (
    <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-8 w-8 text-blue-600 dark:text-blue-400"
      >
        <rect width="20" height="16" x="2" y="4" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </svg>
    </div>
  );
}
